/**
 * LoginPage
 *
 * Centered card with:
 *  - DocIntel branding (purple "D" icon + title)
 *  - Google Sign-In button via @react-oauth/google
 *  - Restricted-access footer text
 *
 * On successful Google auth the user is redirected to the appropriate dashboard.
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';

import { useAuth } from './AuthProvider';

export default function LoginPage() {
  const { login, error, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  // If already authenticated, redirect to the page they were on (when set by the
  // 401 interceptor), falling back to the role-appropriate dashboard.
  React.useEffect(() => {
    if (isAuthenticated) {
      const returnTo = searchParams.get('returnTo');
      const fallback = isAdmin ? '/admin/dashboard' : '/portal/dashboard';
      // Refuse to bounce back to /login itself.
      const dest = returnTo && returnTo.startsWith('/') && !returnTo.startsWith('/login') ? returnTo : fallback;
      navigate(dest, { replace: true });
    }
  }, [isAuthenticated, isAdmin, navigate, searchParams]);

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (!response.credential) return;
    setLoading(true);
    try {
      await login(response.credential);
    } catch {
      // Error surfaced via AuthProvider.error
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="background.default"
      sx={{
        backgroundImage: 'radial-gradient(circle at 50% 0%, rgba(86, 84, 168, 0.06) 0%, transparent 50%)',
      }}
    >
      <Card sx={{ maxWidth: 420, width: '100%', mx: 2, boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          {/* Logo */}
          <Box
            sx={{
              width: 56,
              height: 56,
              bgcolor: 'primary.main',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
            aria-hidden="true"
          >
            <Typography
              variant="h4"
              sx={{ color: 'primary.contrastText', fontWeight: 900 }}
            >
              D
            </Typography>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }} gutterBottom>
            DocIntel
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Intelligent Document Processing Platform
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
              {error}
            </Alert>
          )}

          {loading && (
            <Box display="flex" justifyContent="center" mb={2}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Google Sign-In */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => { /* handled by AuthProvider */ }}
              theme="outline"
              size="large"
              text="signin_with"
              shape="rectangular"
              width="320"
            />
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Access is restricted to authorized Stanford users.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
